import { useEffect } from "react";

import { Box, ThemeProvider } from "@mui/material";

import { mergeMainTheme } from "@aclymatepackages/themes";

import Header from "./Header";
import Footer from "./Footer";
import ReportPagePaper from "./ReportPagePaper";

import { useDynamicWrappingPages } from "../../../helpers/components/report";

const ReportPageLayout = ({
  showHeader = true,
  startDate,
  endDate,
  content,
  blocks,
  setRefElement,
  pdfLoading,
  pageNumber,
  totalPages,
}) => {
  return (
    <ThemeProvider theme={mergeMainTheme()}>
      <ReportPagePaper pdfLoading={pdfLoading}>
        {showHeader && <Header startDate={startDate} endDate={endDate} />}
        <Box
          id="content-box"
          flexGrow={1}
          px={6}
          py={3}
          sx={{ boxSizing: "border-box" }}
        >
          {content}
          {blocks && (
            <>
              {blocks.map((block, idx) => (
                <div
                  ref={(element) =>
                    setRefElement && setRefElement(element, idx)
                  }
                  key={`content-block-${idx}`}
                >
                  {block}
                </div>
              ))}
            </>
          )}
        </Box>
        <Footer pageNumber={pageNumber} totalPages={totalPages} />
      </ReportPagePaper>
    </ThemeProvider>
  );
};

const ReportPage = ({
  pdfLoading,
  content,
  blocks = [],
  pageNumberOffset = 0,
  onPhysicalPageCountChange,
  ...otherProps
}) => {
  const { pageBlocks, setRefElement } = useDynamicWrappingPages(blocks);

  // Blocks haven't been measured/split into physical pages yet on first
  // render, so report a physical page count of 1 until useDynamicWrappingPages
  // settles — content-only entries (no blocks) are always exactly 1 page.
  const physicalPageCount = blocks.length ? Math.max(pageBlocks.length, 1) : 1;

  useEffect(() => {
    onPhysicalPageCountChange && onPhysicalPageCountChange(physicalPageCount);
  }, [physicalPageCount, onPhysicalPageCountChange]);

  if (!blocks || pageBlocks.length <= 1) {
    return (
      <ReportPageLayout
        pdfLoading={pdfLoading}
        content={content}
        blocks={blocks}
        setRefElement={setRefElement}
        {...otherProps}
        pageNumber={pageNumberOffset + 1}
      />
    );
  }

  return (
    <>
      {pageBlocks.map((blocks, idx) => (
        <ReportPageLayout
          pdfLoading={pdfLoading}
          blocks={blocks}
          {...otherProps}
          pageNumber={pageNumberOffset + idx + 1}
          key={`block-page-${idx}`}
        />
      ))}
    </>
  );
};

export default ReportPage;
